import { ZERO, clampPos, kop, maxM, sub, share, type Money } from './money.js';
import { diffDays, nextOccurrenceInclusive, type PlainDate } from './date.js';
import type { Account, CreditTerms } from './types.js';

export interface CreditState {
  accountId: string;
  name: string;
  /** Текущая задолженность, положительным числом. */
  debt: Money;
  limit: Money;
  availableLimit: Money;
  /** Сколько нужно внести, чтобы сохранить беспроцентный период. */
  needForGrace: Money;
  reserved: Money;
  /** Недобор резерва. Именно он вычитается из «Можно потратить». */
  missing: Money;
  /**
   * Минимальный платёж по тарифу карты.
   *
   * `null` — условие не названо. Это не ноль: ноль означал бы «платить не
   * нужно», а мы просто не знаем сколько, и выдумывать нельзя — число уходит
   * в обязательный минимум плана.
   */
  minPayment: Money | null;
  paymentDate: PlainDate;
  daysToPayment: number;
  /**
   * Деньги под платёж собраны: внесено на карту плюс закреплено конвертами.
   * Заполняется `computeAvailable`, которая одна знает про конверты.
   */
  graceSecured: boolean;
  /**
   * Платёж по выписке **фактически внесён** на карту целиком.
   *
   * Отдельно от `graceSecured`: конверт на дебетовой карте обеспечивает
   * платёж, но не выполняет его. Смешение этих двух признаков и было
   * дефектом — экран писал «оплачен», хотя перевода не было.
   */
  settled: boolean;
}

/**
 * Кредитка — единственное место, где ручные трекеры systematically врут.
 * Ключевое различение: **платёж** для сохранения грейса это одна сумма,
 * а вычитать из доступного нужно только **недобор** резерва. Иначе цифра
 * падает дважды: сначала когда деньги зарезервировали, потом когда посчитали
 * весь платёж заново.
 */
export function creditState(
  account: Account,
  terms: CreditTerms,
  today: PlainDate,
): CreditState {
  const debt = clampPos(kop(-account.balance));
  const needForGrace = clampPos(sub(terms.statementDebt, terms.reserved));
  const paymentDate = nextOccurrenceInclusive(today, terms.paymentDay);
  // Ни доли, ни порога — условие неизвестно. Подставить типовые «5%» значит
  // придумать человеку обязательный платёж, которого ему никто не называл.
  const knowsMin = terms.minPaymentPct !== undefined || terms.minPaymentFloor !== undefined;
  const minPayment = knowsMin
    ? maxM(share(terms.statementDebt, terms.minPaymentPct ?? 0), terms.minPaymentFloor ?? ZERO)
    : null;

  return {
    accountId: account.id,
    name: account.name,
    debt,
    limit: terms.limit,
    availableLimit: clampPos(sub(terms.limit, debt)),
    needForGrace: terms.statementDebt,
    reserved: terms.reserved,
    missing: needForGrace,
    minPayment: minPayment === null ? null : (terms.statementDebt === ZERO ? ZERO : minPayment),
    paymentDate,
    daysToPayment: diffDays(today, paymentDate),
    // Без знания о конвертах обеспеченность равна оплаченности; итоговое
    // значение проставит `computeAvailable`.
    graceSecured: needForGrace === ZERO,
    settled: needForGrace === ZERO,
  };
}
